import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, ChevronsUpDown } from "lucide-react";
import { Button } from "../../ui/button";
import { cn } from "../../utils/common/cn";
import type { RiskGrade } from "../../pages/AthenaPricingEngine/lib/api/state-of-play";
import {
  amountLabel,
  type RavBasis,
  type SegmentNode,
} from "../../pages/AthenaPricingEngine/lib/api/segments";

const GRADE_TOKEN: Record<RiskGrade, string> = {
  A: "var(--grade-a)",
  B1: "var(--grade-b1)",
  B2: "var(--grade-b2)",
  B3: "var(--grade-b3)",
  Other: "var(--muted-foreground)",
};

function formatFinanced(value: number) {
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`;
  return `$${Math.round(value / 1_000_000)}M`;
}

const pct = (value: number) => `${value.toFixed(2)}%`;

function rowLabel(row: SegmentNode) {
  return row.grade ? `Grade ${row.grade}` : (row.industry ?? row.office ?? "");
}

function ConfidenceBadge({ tier }: { tier: string }) {
  const own = tier === "Own data";
  return (
    <span
      className={cn(
        "inline-flex items-center border px-2 py-0.5 text-[11px] font-medium",
        own
          ? "border-primary/40 bg-primary/10 text-primary"
          : "border-border bg-secondary text-muted-foreground",
      )}
    >
      {own ? "Own Data" : `Blended with ${tier}`}
    </span>
  );
}

const helper = createColumnHelper<SegmentNode>();

export function SegmentsTable({
  rows,
  basis,
  focus,
}: {
  rows: SegmentNode[];
  basis: RavBasis;
  /** Bumped nonce + row id: expands, scrolls to, and highlights that row. */
  focus?: { id: string; nonce: number } | null;
}) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [sorting, setSorting] = useState<SortingState>([]);
  const [highlighted, setHighlighted] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!focus) return;
    setExpanded((prev) => ({ ...prev, [focus.id]: true }));
    setHighlighted(focus.id);
    const node = containerRef.current?.querySelector(`[data-row-id="${focus.id}"]`);
    node?.scrollIntoView({ behavior: "smooth", block: "center" });
    const timer = window.setTimeout(() => setHighlighted(null), 2400);
    return () => window.clearTimeout(timer);
  }, [focus?.id, focus?.nonce]);

  const columns = useMemo<ColumnDef<SegmentNode, any>[]>(
    () => [
      helper.display({
        id: "segment",
        header: "Segment",
        // No accessor to sort by natively -- compares the same label the
        // cell renders (row's grade/industry/office, whichever applies).
        sortingFn: (rowA, rowB) => rowLabel(rowA.original).localeCompare(rowLabel(rowB.original)),
        cell: ({ row }) => {
          const canExpand = row.getCanExpand();
          const grade = row.original.grade;
          return (
            <div
              className="flex items-center gap-2"
              style={{ paddingLeft: `${row.depth * 18}px` }}
            >
              {canExpand ? (
                <button
                  type="button"
                  onClick={row.getToggleExpandedHandler()}
                  aria-label={row.getIsExpanded() ? "Collapse Row" : "Expand Row"}
                  className="flex size-5 items-center justify-center border border-border text-muted-foreground hover:text-foreground"
                >
                  {row.getIsExpanded() ? (
                    <ChevronDown className="size-3" />
                  ) : (
                    <ChevronRight className="size-3" />
                  )}
                </button>
              ) : (
                <span className="size-5" />
              )}
              {grade ? (
                <span
                  className="size-2"
                  style={{ backgroundColor: GRADE_TOKEN[grade] }}
                  aria-hidden
                />
              ) : null}
              <span
                className={cn(
                  "text-sm",
                  row.depth === 0 ? "font-semibold text-foreground" : "text-foreground/90",
                )}
                style={grade ? { color: GRADE_TOKEN[grade] } : undefined}
              >
                {rowLabel(row.original)}
              </span>
            </div>
          );
        },
      }),
      helper.accessor("financed", {
        header: amountLabel(basis),
        cell: (info) => formatFinanced(info.getValue()),
      }),
      helper.accessor("yield", { header: "Yield", cell: (info) => pct(info.getValue()) }),
      helper.accessor("ftp", { header: "FTP", cell: (info) => pct(info.getValue()) }),
      helper.accessor("expectedLoss", {
        header: "Expected Loss",
        cell: (info) => pct(info.getValue()),
      }),
      helper.accessor("opex", { header: "OpEx", cell: (info) => pct(info.getValue()) }),
      helper.accessor("externalCommission", {
        header: "Ext Comm",
        cell: (info) => pct(info.getValue()),
      }),
      helper.accessor("requiredYield", {
        header: "Required Yield",
        cell: (info) => pct(info.getValue()),
      }),
      helper.accessor("grossRav", {
        header: "Gross RAV",
        cell: (info) => pct(info.getValue()),
      }),
      helper.accessor("internalCommission", {
        header: "Int Comm",
        cell: (info) => (
          <span className="flex flex-col items-end leading-tight">
            <span>{pct(info.getValue())}</span>
            <span className="text-[10px] text-muted-foreground">
              {formatFinanced(info.row.original.internalCommissionDollars)} ·{" "}
              {info.row.original.internalCommissionRate.toFixed(1)}%
            </span>
          </span>
        ),
      }),
      helper.accessor("netRav", {
        header: "Net RAV",
        cell: (info) => (
          <span
            className={cn(
              "font-semibold",
              info.getValue() < 0 ? "text-destructive" : "text-foreground",
            )}
          >
            {pct(info.getValue())}
          </span>
        ),
      }),
      helper.display({
        id: "confidence",
        header: "Confidence",
        enableSorting: false,
        cell: ({ row }) => {
          const { tier } = row.original.credibility;
          return (
            <div className="flex flex-col items-start gap-1">
              <ConfidenceBadge tier={tier} />
              {tier !== "Own data" ? (
                <Link
                  to={`/risk-pricing-engine/calibration?segment=${encodeURIComponent(row.original.id)}`}
                  className="text-[11px] text-primary underline-offset-2 hover:underline"
                >
                  Why this number →
                </Link>
              ) : null}
            </div>
          );
        },
      }),
      helper.display({
        id: "action",
        header: "Action",
        enableSorting: false,
        cell: ({ row }) => {
          const params = new URLSearchParams();
          if (row.original.office) params.set("office", row.original.office);
          if (row.original.industry) params.set("industry", row.original.industry);
          return (
            <Button asChild size="sm" variant="outline" className="h-8 text-xs">
              <Link to={`/risk-pricing-engine/manage-targets?${params.toString()}`}>
                Set Targets
              </Link>
            </Button>
          );
        },
      }),
    ],
    [basis],
  );

  const table = useReactTable({
    data: rows,
    columns,
    state: { expanded, sorting },
    onExpandedChange: setExpanded as never,
    onSortingChange: setSorting,
    getSubRows: (row: SegmentNode) => row.subRows,
    getRowId: (row: SegmentNode, index: number, parent?: { id: string }) =>
      parent ? `${parent.id}>${row.id}` : row.id,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div ref={containerRef} className="overflow-x-auto border border-border bg-card">
      <table className="w-full min-w-[1320px] border-collapse text-sm">
        <thead>
          {table.getHeaderGroups().map((group) => (
            <tr key={group.id} className="border-b border-border bg-secondary/50">
              {group.headers.map((header, i) => {
                const canSort = header.column.getCanSort();
                const sortDir = header.column.getIsSorted();
                return (
                  <th
                    key={header.id}
                    className={cn(
                      "px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground",
                      i === 0 || i > 9 ? "text-left" : "text-right",
                    )}
                  >
                    {canSort ? (
                      <button
                        type="button"
                        onClick={header.column.getToggleSortingHandler()}
                        className={cn(
                          "inline-flex items-center gap-1 hover:text-foreground",
                          i > 0 && i <= 9 ? "flex-row-reverse" : "",
                        )}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {sortDir === "asc" ? (
                          <ArrowUp className="size-3" />
                        ) : sortDir === "desc" ? (
                          <ArrowDown className="size-3" />
                        ) : (
                          <ChevronsUpDown className="size-3 opacity-40" />
                        )}
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => {
            const fixed = row.original.scope === "fixed";
            const cells = row.getVisibleCells();
            return (
              <tr
                key={row.id}
                data-row-id={row.id}
                className={cn(
                  "border-b border-border/60 last:border-b-0",
                  row.depth === 0 ? "bg-card" : row.depth === 1 ? "bg-card/60" : "bg-secondary/20",
                  highlighted === row.id && "bg-amber-dim ring-1 ring-primary",
                )}
              >
                {cells.map((cell, i) => {
                  const isRavColumn = i >= 2 && i <= 9;
                  if (fixed && isRavColumn) {
                    if (i !== 2) return null;
                    return (
                      <td
                        key={cell.id}
                        colSpan={8}
                        className="px-3 py-2.5 text-center text-xs italic text-muted-foreground"
                      >
                        Fixed pricing — not risk-based
                      </td>
                    );
                  }
                  return (
                    <td
                      key={cell.id}
                      className={cn(
                        "px-3 py-2.5 align-middle",
                        i === 0 || i > 9 ? "text-left" : "text-right tabular-nums",
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
